import { useAllMembersQuery, useGetStatsQuery } from '..'
import {
  useAllCountriesQuery,
  useAllCountriesWithCurrenciesQuery,
  useAllCurrenciesQuery,
  useClosestExchangeRateQuery,
  useGetCurrencyQuery,
  useGetGivingSeasonStatsQuery,
  useGetFundraiserStatsQuery,
} from '../graphql'
import {
  flattenGQLArrayResult,
  makeQueryWithReducer,
  parseReducerResult,
} from '../utils'
import { ExchangeRateParser } from './validations'

export * from './types'
export * from './validations'

export const useAllCurrenciesQueryWithReducer = makeQueryWithReducer(
  useAllCurrenciesQuery,
  (result) => flattenGQLArrayResult(result.data?.Currencies),
)

export const useAllCountriesQueryWithReducer = makeQueryWithReducer(
  useAllCountriesQuery,
  (result) => flattenGQLArrayResult(result.data?.Countries),
)

export const useAllCountriesWithCurrenciesQueryWithReducer =
  makeQueryWithReducer(useAllCountriesWithCurrenciesQuery, (result) =>
    flattenGQLArrayResult(result.data?.Countries)?.map((country) => ({
      ...country,
      currencies: country.currencies.map((currency) => currency.currencyCode),
    })),
  )

export const useGetCurrencyQueryWithReducer = makeQueryWithReducer(
  useGetCurrencyQuery,
  (result) => result.data?.Currency,
)

export const useClosestExchangeRateQueryWithReducer = makeQueryWithReducer(
  useClosestExchangeRateQuery,
  parseReducerResult((result) => result.data?.ExchangeRate, ExchangeRateParser),
)

export const useGetGivingSeasonStatsQueryWithReducer = makeQueryWithReducer(
  useGetGivingSeasonStatsQuery,
  (result) => flattenGQLArrayResult(result.data?.GivingSeasonStats),
)

export const useGetFundraiserStatsQueryWithReducer = makeQueryWithReducer(
  useGetFundraiserStatsQuery,
  (result) => flattenGQLArrayResult(result.data?.FundraiserStats),
)

export const useGetStatsQueryWithReducer = makeQueryWithReducer(
  useGetStatsQuery,
  (result) => {
    if (!result.data) {
      return undefined
    }
    return {
      GivingWhatWeCanPledge: {
        totalCount: result.data.GivingWhatWeCanPledge?.totalCount,
        lastRank:
          result.data.GivingWhatWeCanPledge?.edges[0]?.node?.rankByPledgeType,
      },
      TryGivingPledgeCount: {
        totalCount: result.data.TryGivingPledge?.totalCount,
        lastRank: result.data.TryGivingPledge?.edges[0]?.node?.rankByPledgeType,
      },
      CompletedTryGivingPledge: {
        totalCount: result.data.CompletedTryGivingPledge?.totalCount,
      },
      ProjectedDonations: {
        projectedDonationsTotal:
          result.data.ProjectedDonations?.projectedDonationsTotal,
        projectedIncomeTotal:
          result.data.ProjectedDonations?.projectedIncomeTotal,
        numPledges: result.data.ProjectedDonations?.numPledges,
      },
      TotalGWWCDonations: result.data.TotalGWWCDonations,
      TotalCompletionPayments: result.data.TotalCompletionPayments,
      GWWCMemberCountries: result.data.GWWCMemberCountries?.edges.map(
        (edge) => ({
          count: edge.node?.count,
          countryCode: edge.node?.countryCode,
        }),
      ),
    }
  },
)

export const useAllMembersWithReducer = makeQueryWithReducer(
  useAllMembersQuery,
  (result) => {
    return {
      GivingWhatWeCanPledges: flattenGQLArrayResult(
        result.data?.GivingWhatWeCanPledges,
      ),
      TryGivingPledges: flattenGQLArrayResult(result.data?.TryGivingPledges),
      InactiveTryGivingPledgesCount: result.data?.InactiveTryGivingPledgesCount?.totalCount,
    }
  },
)
